import { useEffect, useState, useMemo } from 'react';
import { useWebSockets } from 'src/contexts/webSocketsContext/websockets.context';
import { convertToNumber, HEIGHT, WET_WELL_TAG_NAME } from 'src/features';
import { AssetType } from 'src/types/assets';
import { ReadingItem } from 'src/types/readings/readings';
import { useGetLatestGatewayReadingsQuery } from '../reactQueryApiHooks/useGetLatestGatewayReadingsQuery';

type useWaterDataTypes = {
  asset?: AssetType;
};

export const useWaterData = ({ asset }: useWaterDataTypes) => {
  const [waterLevel, setWaterLevel] = useState<number | null>(null);
  const [unit, setUnit] = useState<string | null>(null);

  const assetAssignedTag = useMemo(
    () => asset?.assignedTags?.find((tag) => tag?.tagName.toLowerCase() === WET_WELL_TAG_NAME),
    [asset]
  );

  const assignedTagId = assetAssignedTag?.assignedTagId;
  const gatewayId = assetAssignedTag?.assignedGatewayId;

  // REST API
  const { latestReading } = useGetLatestGatewayReadingsQuery({
    gatewayId,
    tagId: assignedTagId,
  });

  const restValue = useMemo(() => {
    if (!assignedTagId) return null;

    const reading = latestReading[assignedTagId];
    if (!reading?.values) return null;

    const firstValueRaw = Object.values(reading.values)[0];
    const firstValue = typeof firstValueRaw === 'number' ? firstValueRaw : null;

    return firstValue !== null ? firstValue : null;
  }, [latestReading, assignedTagId]);

  // WebSockets
  const { messages } = useWebSockets();
  const getGatewayInputs = (): ReadingItem[] | undefined => {
    if (!messages) return undefined;

    if (Array.isArray(messages)) {
      if (!gatewayId) return undefined;
      const matchedMessage = messages.find((msg: any) => msg?.data?.sourceId === gatewayId);
      return matchedMessage?.data?.data;
    }

    if (messages?.data?.data) {
      if (gatewayId) {
        if (messages?.data?.sourceId === gatewayId) {
          return messages.data.data;
        }
        return undefined;
      }
      return messages.data.data;
    }

    return undefined;
  };

  const gatewayInputs = getGatewayInputs();

  const wsInput: ReadingItem | undefined = gatewayInputs?.find(
    (input) => input.id === assignedTagId
  );
  const assetProperty = asset?.assetProperties?.find((prop) => prop.name === HEIGHT);

  useEffect(() => {
    if (wsInput) {
      const value = convertToNumber(wsInput.value);
      if (value !== false && value !== null) {
        setWaterLevel(value);
        setUnit(wsInput.rawUnit ?? null);
        return;
      }
    }

    if (restValue !== null) {
      setWaterLevel(restValue);
      setUnit(null);
    }
  }, [wsInput, restValue]);

  useEffect(() => {
    if (!asset) {
      setWaterLevel(null);
      setUnit(null);
    }
  }, [asset]);

  return {
    waterLevel: waterLevel !== null ? Number(waterLevel) : null,
    height: assetProperty?.value ? Number(assetProperty.value) : waterLevel,
    unit,
  };
};
